/**
 * Composio Frontend Service
 *
 * Handles Composio connection flows alongside existing Nango service.
 * All API calls go through apiService for auth header injection.
 */

import apiService from "./apiService";

export interface ComposioConnection {
  provider: string;
  status: string;
  composio_connected_account_id?: string;
  connected_at?: string;
  last_refreshed_at?: string;
}

export interface ComposioConnectionStatus {
  connected: boolean;
  provider: string;
  status?: string;
  connectedAccountId?: string;
  error?: string;
}

export interface ComposioToolkit {
  appId: string;
  name: string;
  description?: string;
  logo?: string;
  categories?: string[];
  authSchemes?: string[];
}

export class ComposioService {
  /**
   * Initiate a Composio OAuth connection for a provider.
   * Returns a redirect URL that the caller should open (new tab or popup).
   */
  static async initiateConnection(
    provider: string,
    callbackUrl?: string
  ): Promise<{ redirectUrl: string; connectionId?: string }> {
    const response = await apiService.post("/api/composio/connect", {
      provider,
      // Land the popup on the dedicated closer page, not on /integrations.
      // Pointing it at a real app route rendered the whole Integrations page
      // inside the 500x700 popup and left it open on top of whatever flow the
      // user was in. See pages/oauth/ComposioCallback.tsx.
      callbackUrl:
        callbackUrl ||
        `${window.location.origin}/oauth/composio/callback?provider=${encodeURIComponent(provider)}`,
    });
    // Controller returns { success, data: { redirectUrl, ... } }
    return response.data.data || response.data;
  }

  /**
   * List all Composio connections for the current user.
   */
  static async listConnections(): Promise<ComposioConnection[]> {
    const response = await apiService.get("/api/composio/connections");
    // Controller returns { success, data: [...] }. Reading `.connections` here
    // meant this always resolved to [].
    return response.data.data || response.data.connections || [];
  }

  /**
   * Get a specific Composio connection by provider.
   */
  static async getConnection(provider: string): Promise<ComposioConnection | null> {
    try {
      const response = await apiService.get(`/api/composio/connections/${provider}`);
      // Controller returns { success, data: {...} } — `.connection` was never set.
      return response.data.data || response.data.connection || null;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  /**
   * Get connection status for a provider.
   *
   * Throws on transport/server errors instead of reporting "not connected".
   * Collapsing failures into `{ connected: false }` made a 500 indistinguishable
   * from a genuine not-connected result, so callers polling this endpoint just
   * timed out silently with no clue why.
   */
  static async getConnectionStatus(provider: string): Promise<ComposioConnectionStatus> {
    const response = await apiService.get(`/api/composio/connections/${provider}/status`);
    return (
      response.data.data ||
      response.data.status || { connected: false, provider }
    );
  }

  /**
   * Delete (disconnect) a Composio connection.
   */
  static async deleteConnection(provider: string): Promise<boolean> {
    try {
      const response = await apiService.delete(`/api/composio/connections/${provider}`);
      return response.data.success === true;
    } catch {
      return false;
    }
  }

  /**
   * Refresh a Composio connection's credentials.
   */
  static async refreshConnection(provider: string): Promise<boolean> {
    try {
      const response = await apiService.post(`/api/composio/connections/${provider}/refresh`);
      return response.data.success === true;
    } catch {
      return false;
    }
  }

  /**
   * List available Composio toolkits (apps that can be connected).
   */
  static async listToolkits(): Promise<ComposioToolkit[]> {
    const response = await apiService.get("/api/composio/toolkits");
    return response.data.data || response.data.toolkits || [];
  }

  /**
   * Search Composio toolkits by query string.
   */
  static async searchToolkits(query: string): Promise<ComposioToolkit[]> {
    const response = await apiService.get("/api/composio/toolkits/search", {
      params: { q: query },
    });
    return response.data.data || response.data.toolkits || [];
  }
}

export default ComposioService;
